import { ComponentProps, JSX } from 'react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'radix-ui';
import { TranslationKey } from '../../utils/i18n.ts';
import { fallbackFontFamilies } from '../../styles/consts.ts';
import { resetButtonStyles } from '../../styles/helpers.ts';

export type DesktopTabsProps = {
  tabs: Array<
    {
      content: JSX.Element;
    } & (
      | {
          label: TranslationKey;
        }
      | {
          icon: string;
        }
    )
  >;
  prefix?: string;
} & ComponentProps<typeof Tabs.Root>;

const DesktopTabs = ({
  tabs,
  prefix = defPrefix,
  ...props
}: DesktopTabsProps) => {
  const { t } = useTranslation();
  return (
    <Tabs.Root defaultValue={prefix + '1'} {...props}>
      <List aria-label="Tabs">
        {tabs.map((data, index) => {
          return (
            <Trigger
              key={'label' in data ? data.label : data.icon}
              value={prefix + `${index + 1}`}
              $iconTrigger={!('label' in data)}
            >
              {'label' in data ? (
                t(data.label)
              ) : (
                <TriggerIcon src={data.icon} alt={'Icon'} />
              )}
            </Trigger>
          );
        })}
      </List>
      {tabs.map((data, index) => (
        <Content
          key={'label' in data ? data.label : data.icon}
          value={prefix + `${index + 1}`}
        >
          {data.content}
        </Content>
      ))}
    </Tabs.Root>
  );
};

const List = styled(Tabs.List)`
  display: flex;
  gap: ${({ theme }) => theme.responsive(8)};
`;

const borderRadius = 12;

const Trigger = styled(Tabs.Trigger)<{
  $iconTrigger: boolean;
}>`
  ${resetButtonStyles};
  flex: ${({ $iconTrigger }) => ($iconTrigger ? '0 0 auto' : '1 1 50%')};
  border: 3px solid ${({ theme }) => theme.getColor('textDark')};
  border-bottom-width: 0;
  border-top-left-radius: ${borderRadius}px;
  border-top-right-radius: ${borderRadius}px;
  padding: ${({ theme }) => theme.spacing(15, 20)};
  font-size: ${({ theme }) => theme.responsive(21)};
  line-height: 1;
  font-weight: 700;
  font-family: Inter, ${fallbackFontFamilies};
  transition:
    background-color 0.4s ease,
    color 0.4s ease,
    border-color 0.4s ease;
  color: ${({ theme }) => theme.getColor('accentSecondary')};
  position: relative;
  z-index: 1;

  &[data-state='active'] {
    background-color: ${({ theme }) => theme.getColor('bgCards')};
    color: ${({ theme }) => theme.getColor('completelyWhite')};
  }
`;

const TriggerIcon = styled.img`
  width: ${({ theme }) => theme.responsive(22)};
  height: ${({ theme }) => theme.responsive(22)};
  object-fit: contain;
  object-position: center;
`;

const Content = styled(Tabs.Content)`
  padding: ${({ theme }) => theme.spacing(10, 16)};
  margin-top: -3px;
  border: 3px solid ${({ theme }) => theme.getColor('textDark')};
  border-bottom-left-radius: ${borderRadius}px;
  border-bottom-right-radius: ${borderRadius}px;
  background-color: ${({ theme }) => theme.getColor('bgCards')};
`;

const defPrefix = 'tab';

export { DesktopTabs };
